/**
 * WT1 - the walk-through queue on the phone. Contract: docs/planning/2026-09-23-wt1-build-plan.md section D;
 * design: docs/superpowers/specs/2026-09-20-wt1-walkthrough-capture-design.md section 6.
 *
 * "Offline capture, online binding" (his decision, 2026-09-20): the tour is started with signal, and from
 * then on every capture is DURABLE WITH NONE. Tilt-up concrete kills signal inside; the lot has it.
 *
 * ⭐ IndexedDB, not localStorage: localStorage is ~5 MB of strings; a 40-photo tour is ~25 MB of bytes. The
 *    bytes are stored as an ArrayBuffer plus its content type, not a Blob - Safari's Blob-in-IndexedDB has a
 *    history of coming back empty.
 *
 * ⭐⭐ The three rules that make the drain correct (spec section 6):
 *   1. The signed upload url is requested at DRAIN time, FRESH for every attempt, and never stored. It
 *      expires; one fetched before walking into the building is dead by the time signal returns - and that
 *      would look correct in every test written at a desk. A PUT refused with 400/401/403 gets one more fresh
 *      url inside the same attempt.
 *   2. The photo id is made HERE (lib/uuid.ts), so a retry after a dropped response lands as the SAME row.
 *   3. Serial, one upload in flight, a lock per tour. On one bar, concurrent uploads fight each other.
 *
 * ⛔ The service worker (public/sw.js) is NOT involved and must not be: it deliberately never intercepts
 *    non-GET or /api/, and iOS Safari has no Background Sync, so moving the drain there buys nothing and
 *    spends a working safety rule. The drain is app code, run by the capture screen while it is open (mount,
 *    the `online` event, after each capture, every 20 s).
 *
 * ⛔ Unreadable is unknown, never empty: a failure keeps its text in `lastError` and it is SHOWN. Nothing here
 *    swallows a server's refusal.
 */

import { NetworkError } from './api';
import { isTagCode, isTagSet, type TagCode, type TagSet } from './tourTags';
import { newUuid } from './uuid';

export const TOUR_DB_NAME = 'apex-tours';
const TOUR_DB_VERSION = 1;
const TOURS = 'tours';
const CAPTURES = 'captures';
const BY_TOUR = 'byTour';

/** What a capture waiting only on signal says - plain words, because it is not an error. */
export const WAITING_FOR_SIGNAL = 'Waiting for signal';

export type CaptureState = 'pending' | 'uploading' | 'uploaded' | 'failed';

export interface TourRecord {
  tourId: string;
  masterPropertyId: string;
  propertyLabel: string;
  propertyType: string | null;
  propertyLat: number | null;
  propertyLng: number | null;
  startLat: number | null;
  startLng: number | null;
  touredAt: string;
  /** Null = follow the property's type (tagSetFor). */
  tagSet: TagSet | null;
  tagSetDirty: boolean;
  overallNotes: string;
  notesDirty: boolean;
  /**
   * Bumped by every notes or tag-set edit. The drain clears the dirty flags only when the revision it SENT
   * is still the current one - an edit typed while the PATCH was in flight is never marked as saved.
   */
  notesRevision: number;
  /** The captured count the server last accepted; null = never reported. */
  lastReportedCount: number | null;
  /** The server's refusal of a notes, tag-set or count update, shown on the capture screen. */
  syncError: string | null;
  updatedAt: number;
}

export interface CaptureRecord {
  id: string;
  tourId: string;
  /** The downscaled JPEG (or the original, when it could not be decoded). NULL once uploaded. */
  bytes: ArrayBuffer | null;
  contentType: string;
  fileSize: number;
  /** A small data URL kept after upload, so the grid works offline. */
  thumbnail: string | null;
  subject: TagCode;
  note: string;
  capturedAt: string;
  sortOrder: number;
  state: CaptureState;
  attempts: number;
  lastError: string | null;
  /** Epoch ms; a capture is not tried again before this. */
  nextAttemptAt: number;
  /** An edit (tag or note) made after upload, still to be PATCHed. */
  metaDirty: boolean;
  /** Bumped by every edit; the same in-flight guard as TourRecord.notesRevision. */
  metaRevision: number;
  /**
   * Set just before the save is sent. A save whose answer was lost may have landed, so from then on the
   * photo is treated as possibly on the server: it can no longer be discarded from the phone alone.
   * Optional: records written before this field read as never attempted.
   */
  saveAttempted?: boolean;
}

export interface TourInput {
  tourId: string;
  masterPropertyId: string;
  propertyLabel: string;
  propertyType?: string | null;
  propertyLat?: number | null;
  propertyLng?: number | null;
  startLat?: number | null;
  startLng?: number | null;
  touredAt: string;
  tagSet?: TagSet | null;
  overallNotes?: string | null;
}

export interface NewCapture {
  tourId: string;
  bytes: ArrayBuffer;
  contentType: string;
  thumbnail?: string | null;
  subject: TagCode;
  note?: string;
  /** Defaults to the queue's clock. */
  capturedAt?: string;
  /** Defaults to one past the tour's highest, read inside the same transaction as the write. */
  sortOrder?: number;
}

export interface TourCounts {
  total: number;
  uploaded: number;
  /** Everything not yet uploaded - pending, uploading and failed. This is the number always on screen. */
  outstanding: number;
  failed: number;
}

export interface TourQueue {
  readonly dbName: string;
  now(): number;
  /** Create, or refresh the descriptive fields of, a tour. ⛔ Never overwrites an unsent notes/tag-set edit. */
  saveTour(input: TourInput): Promise<TourRecord>;
  getTour(tourId: string): Promise<TourRecord | null>;
  /** Newest tour first. */
  listTours(): Promise<TourRecord[]>;
  /** ⛔ Resolves only AFTER the IndexedDB transaction has COMPLETED - the capture sheet closes on this. */
  addCapture(input: NewCapture): Promise<CaptureRecord>;
  getCapture(id: string): Promise<CaptureRecord | null>;
  updateCapture(id: string, changes: { note?: string; subject?: TagCode }): Promise<CaptureRecord>;
  /** Refuses once a capture is uploading or uploaded, or its save went out: the server may have it. */
  discardCapture(id: string): Promise<void>;
  /** sortOrder ascending. */
  listCaptures(tourId: string): Promise<CaptureRecord[]>;
  counts(tourId: string): Promise<TourCounts>;
  setNotes(tourId: string, text: string): Promise<TourRecord>;
  setTagSet(tourId: string, set: TagSet | null): Promise<TourRecord>;
  nextSortOrder(tourId: string): Promise<number>;
  close(): void;
}

export interface TourQueueOptions {
  /** Injected in tests (fake-indexeddb); the browser's `indexedDB` otherwise. */
  factory?: IDBFactory;
  /** Called when the connection is lost (the browser closed it, or a newer version took over). */
  onClosed?: () => void;
  name?: string;
  /** Epoch ms. Injected in tests so backoff can be walked without waiting. */
  now?: () => number;
}

/** What the drain needs from the network. The real one is lib/tourTransport.ts. */
export interface TourTransport {
  /** A FRESH signed upload url - asked for on every attempt, never cached. */
  requestUploadUrl(tourId: string, photoId: string, body: { capturedAt: string; contentType: string }): Promise<string>;
  /** PUT the bytes to the signed url. Throws an error carrying `status` on a refusal, NetworkError offline. */
  putBytes(url: string, bytes: ArrayBuffer, contentType: string): Promise<void>;
  savePhoto(
    tourId: string,
    photoId: string,
    body: {
      subject: TagCode;
      note: string | null;
      captured_at: string;
      sort_order: number;
      file_size: number;
      content_type: string;
    },
  ): Promise<void>;
  patchPhoto(tourId: string, photoId: string, body: { subject?: TagCode; note?: string | null }): Promise<void>;
  patchTour(
    tourId: string,
    body: { overall_notes?: string | null; tag_set?: TagSet | null; captured_count?: number },
  ): Promise<void>;
}

export interface DrainSummary {
  uploaded: number;
  failed: number;
  /** The drain stopped because the network was unreachable; everything it touched is back to pending. */
  offline: boolean;
  outstanding: number;
  /** Every refusal met on this pass, in the server's own words. */
  errors: string[];
}

// ---------------------------------------------------------------------------------------------------------
// IndexedDB plumbing
// ---------------------------------------------------------------------------------------------------------

const requestResult = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('The phone storage could not be read'));
  });

/**
 * One transaction, resolved on `complete` - never on a request's success, which is BEFORE the write is
 * durable. `work` sets up its requests with callbacks (no `await` inside a transaction: it auto-commits at
 * the first idle turn) and returns a thunk read at completion. A throw inside a callback aborts the whole
 * transaction and rejects with THAT error, so a refusal ("already uploaded") reaches the caller in words.
 */
const transact = <T>(
  db: IDBDatabase,
  stores: string | string[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction, guard: (fn: () => void) => void) => () => T,
  options?: IDBTransactionOptions,
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    let failure: unknown = null;
    let tx: IDBTransaction;
    try {
      tx = db.transaction(stores, mode, options);
    } catch (err) {
      reject(err);
      return;
    }
    const fail = (err: unknown) => {
      if (failure === null) failure = err ?? new Error('The phone storage refused the change');
      try {
        tx.abort();
      } catch {
        // already finished or aborting
      }
    };
    const guard = (fn: () => void) => {
      try {
        fn();
      } catch (err) {
        fail(err);
      }
    };
    let read: (() => T) | null = null;
    guard(() => {
      read = work(tx, guard);
    });
    tx.oncomplete = () => {
      if (failure !== null) reject(failure);
      else resolve(read ? read() : (undefined as T));
    };
    tx.onabort = () => reject(failure ?? tx.error ?? new Error('The phone storage refused the change'));
  });

const openDatabase = (factory: IDBFactory, name: string, onClosed?: () => void): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = factory.open(name, TOUR_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(TOURS)) db.createObjectStore(TOURS, { keyPath: 'tourId' });
      if (!db.objectStoreNames.contains(CAPTURES)) {
        const captures = db.createObjectStore(CAPTURES, { keyPath: 'id' });
        captures.createIndex(BY_TOUR, 'tourId');
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // A future version opened in another tab must not be blocked by this one.
      db.onversionchange = () => {
        db.close();
        onClosed?.();
      };
      // iOS closes a suspended app's IndexedDB connection ("Connection to Indexed Database server lost");
      // every later write would then refuse until a reload. Forget it, so the next caller opens a fresh one.
      db.onclose = () => onClosed?.();
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error('The phone storage could not be opened'));
  });

const finiteOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

const bySortOrder = (a: CaptureRecord, b: CaptureRecord) =>
  a.sortOrder - b.sortOrder || a.capturedAt.localeCompare(b.capturedAt);

// ---------------------------------------------------------------------------------------------------------
// The queue
// ---------------------------------------------------------------------------------------------------------

interface Lane {
  running: Promise<DrainSummary>;
  waiting: Promise<DrainSummary> | null;
}

/** The drain's own writes, kept off the public TourQueue so a screen cannot mark a photo uploaded. */
interface Internals {
  mutateCapture(id: string, fn: (rec: CaptureRecord) => CaptureRecord): Promise<CaptureRecord | null>;
  mutateTour(tourId: string, fn: (rec: TourRecord) => TourRecord): Promise<TourRecord | null>;
  /** The per-tour lock. Per QUEUE: a page opens one (getTourQueue); a reload opens a new one. */
  lanes: Map<string, Lane>;
}

const internals = new WeakMap<TourQueue, Internals>();

export const openTourQueue = async (opts: TourQueueOptions = {}): Promise<TourQueue> => {
  const factory = opts.factory ?? (typeof indexedDB !== 'undefined' ? indexedDB : undefined);
  if (!factory) {
    throw new Error('This browser has no IndexedDB, so walk-through photos cannot be kept on the phone.');
  }
  const dbName = opts.name ?? TOUR_DB_NAME;
  const now = opts.now ?? (() => Date.now());
  const db = await openDatabase(factory, dbName, opts.onClosed);

  /** Read-modify-write of one record inside one transaction. Missing -> null, nothing written. */
  const mutate = <R>(store: string, key: string, fn: (rec: R) => R): Promise<R | null> =>
    transact<R | null>(db, store, 'readwrite', (tx, guard) => {
      const os = tx.objectStore(store);
      let out: R | null = null;
      const get = os.get(key);
      get.onsuccess = () =>
        guard(() => {
          if (get.result === undefined) return;
          out = fn(get.result as R);
          os.put(out);
        });
      return () => out;
    });

  const getTour = async (tourId: string): Promise<TourRecord | null> =>
    ((await requestResult(db.transaction(TOURS, 'readonly').objectStore(TOURS).get(tourId))) as TourRecord | undefined) ??
    null;

  const listCaptures = async (tourId: string): Promise<CaptureRecord[]> => {
    const rows = (await requestResult(
      db.transaction(CAPTURES, 'readonly').objectStore(CAPTURES).index(BY_TOUR).getAll(tourId),
    )) as CaptureRecord[];
    return rows.sort(bySortOrder);
  };

  const requireTour = (rec: TourRecord | null, tourId: string): TourRecord => {
    if (!rec) throw new Error(`That walk-through (${tourId}) is not on this phone.`);
    return rec;
  };

  const queue: TourQueue = {
    dbName,
    now,

    saveTour: async (input) => {
      if (!input.tourId) throw new Error('A walk-through needs its id before it is kept on the phone.');
      if (input.tagSet != null && !isTagSet(input.tagSet)) throw new Error(`Unknown tag set: ${String(input.tagSet)}`);
      return transact<TourRecord>(db, TOURS, 'readwrite', (tx, guard) => {
        const os = tx.objectStore(TOURS);
        let saved: TourRecord | null = null;
        const get = os.get(input.tourId);
        get.onsuccess = () =>
          guard(() => {
            const prev = get.result as TourRecord | undefined;
            const descriptive = {
              tourId: input.tourId,
              masterPropertyId: input.masterPropertyId,
              propertyLabel: input.propertyLabel,
              propertyType: input.propertyType ?? null,
              propertyLat: finiteOrNull(input.propertyLat),
              propertyLng: finiteOrNull(input.propertyLng),
              startLat: finiteOrNull(input.startLat),
              startLng: finiteOrNull(input.startLng),
              touredAt: input.touredAt,
              updatedAt: now(),
            };
            saved = prev
              ? {
                  ...prev,
                  ...descriptive,
                  // ⛔ The phone's unsent edit wins over the server's copy: a screen that re-reads the tour
                  //    on mount must not erase notes typed with no signal.
                  // `null` from the server is notes CLEARED elsewhere, not notes left out.
                  overallNotes: prev.notesDirty
                    ? prev.overallNotes
                    : input.overallNotes !== undefined ? (input.overallNotes ?? '') : prev.overallNotes,
                  tagSet: prev.tagSetDirty ? prev.tagSet : input.tagSet !== undefined ? input.tagSet : prev.tagSet,
                }
              : {
                  ...descriptive,
                  tagSet: input.tagSet ?? null,
                  tagSetDirty: false,
                  overallNotes: input.overallNotes ?? '',
                  notesDirty: false,
                  notesRevision: 0,
                  lastReportedCount: null,
                  syncError: null,
                };
            os.put(saved);
          });
        return () => saved as TourRecord;
      });
    },

    getTour,

    listTours: async () => {
      const rows = (await requestResult(db.transaction(TOURS, 'readonly').objectStore(TOURS).getAll())) as TourRecord[];
      return rows.sort((a, b) => b.touredAt.localeCompare(a.touredAt));
    },

    addCapture: async (input) => {
      if (!isTagCode(input.subject)) throw new Error(`Unknown photo tag: ${String(input.subject)}`);
      // A tag check rather than `instanceof`: bytes read in another realm (a worker, an iframe) are still bytes.
      if (Object.prototype.toString.call(input.bytes) !== '[object ArrayBuffer]' || input.bytes.byteLength === 0) {
        throw new Error('The photo is empty (0 bytes) - take it again.');
      }
      const capturedAt = input.capturedAt ?? new Date(now()).toISOString();
      if (Number.isNaN(Date.parse(capturedAt))) throw new Error(`Not a capture time: ${capturedAt}`);
      const record: CaptureRecord = {
        id: newUuid(),
        tourId: input.tourId,
        bytes: input.bytes,
        contentType: input.contentType || 'image/jpeg',
        fileSize: input.bytes.byteLength,
        thumbnail: input.thumbnail ?? null,
        subject: input.subject,
        note: input.note ?? '',
        capturedAt,
        sortOrder: input.sortOrder ?? 0,
        state: 'pending',
        attempts: 0,
        lastError: null,
        nextAttemptAt: 0,
        metaDirty: false,
        metaRevision: 0,
        saveAttempted: false,
      };
      return transact<CaptureRecord>(
        db,
        CAPTURES,
        'readwrite',
        (tx, guard) => {
          const os = tx.objectStore(CAPTURES);
          if (input.sortOrder !== undefined) {
            os.add(record);
          } else {
            // The next sort order is read INSIDE the write's transaction, so two captures saved back to back
            // can never both take the same number.
            const siblings = os.index(BY_TOUR).getAll(input.tourId);
            siblings.onsuccess = () =>
              guard(() => {
                const rows = siblings.result as CaptureRecord[];
                record.sortOrder = rows.reduce((max, r) => Math.max(max, r.sortOrder), 0) + 1;
                os.add(record);
              });
          }
          return () => record;
        },
        // 'strict': complete only once the write is flushed to disk. This is the one write that must survive
        // the phone dying in the next second.
        { durability: 'strict' },
      );
    },

    getCapture: async (id) =>
      ((await requestResult(db.transaction(CAPTURES, 'readonly').objectStore(CAPTURES).get(id))) as
        | CaptureRecord
        | undefined) ?? null,

    updateCapture: async (id, changes) => {
      if (changes.subject !== undefined && !isTagCode(changes.subject)) {
        throw new Error(`Unknown photo tag: ${String(changes.subject)}`);
      }
      const updated = await mutate<CaptureRecord>(CAPTURES, id, (rec) => ({
        ...rec,
        note: changes.note ?? rec.note,
        subject: changes.subject ?? rec.subject,
        metaRevision: rec.metaRevision + 1,
        // Before upload the save itself carries the new values; after it, a PATCH must.
        metaDirty: rec.state === 'uploaded' ? true : rec.metaDirty,
        // A refusal about the photo's own tag or note is fixed by this edit: try it now, not after the backoff.
        nextAttemptAt: rec.state === 'failed' ? 0 : rec.nextAttemptAt,
      }));
      if (!updated) throw new Error('That photo is no longer on this phone.');
      return updated;
    },

    discardCapture: async (id) => {
      await transact<void>(db, CAPTURES, 'readwrite', (tx, guard) => {
        const os = tx.objectStore(CAPTURES);
        const get = os.get(id);
        get.onsuccess = () =>
          guard(() => {
            const rec = get.result as CaptureRecord | undefined;
            if (!rec) return;
            if (rec.state === 'uploaded') {
              throw new Error('This photo has already uploaded, so it can no longer be discarded from the phone.');
            }
            if (rec.state === 'uploading') {
              throw new Error('This photo is uploading right now - try again in a moment.');
            }
            if (rec.saveAttempted) {
              // Its save went out and the answer was lost: the app may already hold it, and a phone-only
              // delete would leave a photo in the report that he believes he threw away.
              throw new Error('This photo may already have reached the app, so it stays with the tour.');
            }
            os.delete(id);
          });
        return () => undefined;
      });
    },

    listCaptures,

    counts: async (tourId) => {
      const rows = await listCaptures(tourId);
      const uploaded = rows.filter((r) => r.state === 'uploaded').length;
      return {
        total: rows.length,
        uploaded,
        outstanding: rows.length - uploaded,
        failed: rows.filter((r) => r.state === 'failed').length,
      };
    },

    setNotes: async (tourId, text) => {
      const updated = await mutate<TourRecord>(TOURS, tourId, (rec) =>
        rec.overallNotes === text
          ? rec
          : { ...rec, overallNotes: text, notesDirty: true, notesRevision: rec.notesRevision + 1, updatedAt: now() },
      );
      return requireTour(updated, tourId);
    },

    setTagSet: async (tourId, set) => {
      if (set !== null && !isTagSet(set)) throw new Error(`Unknown tag set: ${String(set)}`);
      const updated = await mutate<TourRecord>(TOURS, tourId, (rec) =>
        rec.tagSet === set
          ? rec
          : { ...rec, tagSet: set, tagSetDirty: true, notesRevision: rec.notesRevision + 1, updatedAt: now() },
      );
      return requireTour(updated, tourId);
    },

    nextSortOrder: async (tourId) => (await listCaptures(tourId)).reduce((max, r) => Math.max(max, r.sortOrder), 0) + 1,

    close: () => db.close(),
  };

  internals.set(queue, {
    mutateCapture: (id, fn) => mutate<CaptureRecord>(CAPTURES, id, fn),
    mutateTour: (tourId, fn) => mutate<TourRecord>(TOURS, tourId, fn),
    lanes: new Map(),
  });

  return queue;
};

let shared: Promise<TourQueue> | null = null;

/**
 * The page's one queue. Every screen drains through it, so the per-tour lock covers them all - two queues
 * on one page would each hold their own lock. It lives as long as the page, unless the browser drops the
 * connection: then the next call opens a new one. ⭐ So screens ask for it per action, never keep it.
 */
export const getTourQueue = (): Promise<TourQueue> => {
  if (!shared) {
    const opening: Promise<TourQueue> = openTourQueue({
      onClosed: () => {
        if (shared === opening) shared = null;
      },
    });
    shared = opening.catch((err) => {
      shared = null;
      throw err;
    });
  }
  return shared;
};

// ---------------------------------------------------------------------------------------------------------
// The drain
// ---------------------------------------------------------------------------------------------------------

/** 5 s, 15 s, 60 s, then every 5 minutes - never given up on, because the photo is his evidence. */
export const backoffMs = (attempts: number): number => [5_000, 15_000, 60_000][Math.max(1, attempts) - 1] ?? 300_000;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/** The HTTP status an error carries (APIError, or any transport error with a numeric `status`). */
const statusOf = (err: unknown): number | null => (isRecord(err) && typeof err.status === 'number' ? err.status : null);

/**
 * The browsers' own words for a fetch that never reached a server (Chrome, Safari, Firefox, React Native,
 * Node). ⛔ Matched on purpose rather than treating EVERY TypeError as offline: a bug ("x is not a
 * function") must surface as a failure with its text, not hide forever behind "Waiting for signal".
 */
const FETCH_FAILURE = /failed to fetch|load failed|networkerror|network request failed|fetch failed/i;

type Failure = { kind: 'offline' } | { kind: 'upload_missing'; message: string } | { kind: 'refused'; message: string };

const messageOf = (err: unknown): string => {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string' && err) return err;
  return 'The upload failed and the server gave no reason.';
};

export const classifyDrainError = (err: unknown): Failure => {
  if (err instanceof NetworkError) return { kind: 'offline' };
  if (err instanceof TypeError && FETCH_FAILURE.test(err.message)) return { kind: 'offline' };
  const body = isRecord(err) ? err.body : undefined;
  if (isRecord(body) && body.code === 'upload_missing') return { kind: 'upload_missing', message: messageOf(err) };
  return { kind: 'refused', message: messageOf(err) };
};

/** A signed url past its shelf life is refused with one of these; a fresh url is worth exactly one more try. */
const STALE_URL_STATUSES = new Set([400, 401, 403]);

const noteOrNull = (note: string): string | null => (note.trim() ? note : null);

type UploadOutcome = { kind: 'uploaded' } | { kind: 'offline' } | { kind: 'failed'; message: string } | { kind: 'retry' } | { kind: 'skipped' };

const uploadOne = async (
  inner: Internals,
  transport: TourTransport,
  tourId: string,
  id: string,
  now: () => number,
): Promise<UploadOutcome> => {
  // Claim it. A capture already uploaded (by a pass that ran in between) is left alone.
  const claimed = await inner.mutateCapture(id, (rec) => (rec.state === 'uploaded' ? rec : { ...rec, state: 'uploading' }));
  if (!claimed || claimed.state !== 'uploading') return { kind: 'skipped' };

  const refuse = async (message: string): Promise<UploadOutcome> => {
    await inner.mutateCapture(id, (rec) => {
      const attempts = rec.attempts + 1;
      return { ...rec, state: 'failed', attempts, lastError: message, nextAttemptAt: now() + backoffMs(attempts) };
    });
    return { kind: 'failed', message };
  };

  const bytes = claimed.bytes;
  if (!bytes || bytes.byteLength === 0) {
    return refuse('This photo is missing its picture on this phone, so it cannot be uploaded.');
  }

  try {
    const body = { capturedAt: claimed.capturedAt, contentType: claimed.contentType };
    const url = await transport.requestUploadUrl(tourId, id, body);
    try {
      await transport.putBytes(url, bytes, claimed.contentType);
    } catch (err) {
      const status = statusOf(err);
      if (status === null || !STALE_URL_STATUSES.has(status)) throw err;
      // The shelf-life trap: ONE fresh url, one more PUT, inside this same attempt.
      const fresh = await transport.requestUploadUrl(tourId, id, body);
      await transport.putBytes(fresh, bytes, claimed.contentType);
    }
    await inner.mutateCapture(id, (rec) => (rec.saveAttempted ? rec : { ...rec, saveAttempted: true }));
    await transport.savePhoto(tourId, id, {
      subject: claimed.subject,
      note: noteOrNull(claimed.note),
      captured_at: claimed.capturedAt,
      sort_order: claimed.sortOrder,
      file_size: claimed.fileSize,
      content_type: claimed.contentType,
    });
    await inner.mutateCapture(id, (rec) => ({
      ...rec,
      state: 'uploaded',
      // The bytes go; the thumbnail stays, so the grid still shows it with no signal.
      bytes: null,
      attempts: 0,
      lastError: null,
      nextAttemptAt: 0,
      // An edit made while this upload was in flight did not travel with the save: PATCH it next.
      metaDirty: rec.metaDirty || rec.metaRevision !== claimed.metaRevision,
    }));
    return { kind: 'uploaded' };
  } catch (err) {
    const failure = classifyDrainError(err);
    if (failure.kind === 'offline') {
      // No attempt counted: no signal is not a failure of this photo.
      await inner.mutateCapture(id, (rec) => ({ ...rec, state: 'pending', lastError: WAITING_FOR_SIGNAL }));
      return { kind: 'offline' };
    }
    if (failure.kind === 'upload_missing') {
      // The save found no object behind the key: back to pending, and the next attempt PUTs again. Backed off
      // like any refusal, so a PUT that keeps "succeeding" into nothing cannot spin on one bar.
      await inner.mutateCapture(id, (rec) => {
        const attempts = rec.attempts + 1;
        return { ...rec, state: 'pending', attempts, lastError: failure.message, nextAttemptAt: now() + backoffMs(attempts) };
      });
      return { kind: 'retry' };
    }
    return refuse(failure.message);
  }
};

class DrainStopped extends Error {}

const drainOnce = async (
  queue: TourQueue,
  inner: Internals,
  transport: TourTransport,
  tourId: string,
  now: () => number,
): Promise<DrainSummary> => {
  const summary: DrainSummary = { uploaded: 0, failed: 0, offline: false, outstanding: 0, errors: [] };

  /** Run one metadata call; an offline failure stops the whole drain, a refusal is recorded and returned. */
  const attempt = async (call: () => Promise<void>): Promise<string | null> => {
    try {
      await call();
      return null;
    } catch (err) {
      const failure = classifyDrainError(err);
      if (failure.kind === 'offline') throw new DrainStopped();
      summary.errors.push(failure.message);
      return failure.message;
    }
  };

  let countRefused = false;
  const reportCount = async () => {
    const tour = await queue.getTour(tourId);
    if (!tour || countRefused) return;
    const { total } = await queue.counts(tourId);
    if (tour.lastReportedCount === total) return;
    const refusal = await attempt(() => transport.patchTour(tourId, { captured_count: total }));
    if (refusal) countRefused = true;
    await inner.mutateTour(tourId, (rec) =>
      refusal ? { ...rec, syncError: refusal } : { ...rec, lastReportedCount: total, syncError: null },
    );
  };

  try {
    // The count FIRST as well as last: a few bytes that let the report, at any desk, name how many photos
    // exist - before the first 600 KB upload has a chance to fail on one bar.
    await reportCount();

    const due = (await queue.listCaptures(tourId)).filter((c) => c.state !== 'uploaded' && c.nextAttemptAt <= now());
    for (const capture of due) {
      const outcome = await uploadOne(inner, transport, tourId, capture.id, now);
      if (outcome.kind === 'offline') throw new DrainStopped();
      if (outcome.kind === 'uploaded') summary.uploaded++;
      if (outcome.kind === 'failed') {
        summary.failed++;
        summary.errors.push(outcome.message);
      }
    }

    // Edits made to photos after they uploaded.
    const edited = (await queue.listCaptures(tourId)).filter((c) => c.state === 'uploaded' && c.metaDirty);
    for (const capture of edited) {
      const refusal = await attempt(() =>
        transport.patchPhoto(tourId, capture.id, { subject: capture.subject, note: noteOrNull(capture.note) }),
      );
      await inner.mutateCapture(capture.id, (rec) =>
        refusal
          ? { ...rec, lastError: refusal }
          : { ...rec, lastError: null, metaDirty: rec.metaRevision !== capture.metaRevision },
      );
    }

    // The overall notes and the tag set - only what changed, and cleared only if nothing changed meanwhile.
    const tour = await queue.getTour(tourId);
    if (tour && (tour.notesDirty || tour.tagSetDirty)) {
      const body: { overall_notes?: string | null; tag_set?: TagSet | null } = {};
      if (tour.notesDirty) body.overall_notes = tour.overallNotes.trim() ? tour.overallNotes : null;
      if (tour.tagSetDirty) body.tag_set = tour.tagSet;
      const refusal = await attempt(() => transport.patchTour(tourId, body));
      await inner.mutateTour(tourId, (rec) => {
        if (refusal) return { ...rec, syncError: refusal };
        if (rec.notesRevision !== tour.notesRevision) return { ...rec, syncError: null };
        return { ...rec, notesDirty: false, tagSetDirty: false, syncError: null };
      });
    }

    // And the count again, if a capture was added or discarded while this pass ran.
    await reportCount();
  } catch (err) {
    if (!(err instanceof DrainStopped)) throw err;
    summary.offline = true;
  }

  summary.outstanding = (await queue.counts(tourId)).outstanding;
  return summary;
};

/**
 * Drain one tour: upload what is due, then the edits, then the count. Serial - ONE upload in flight.
 *
 * ⭐ The per-tour lock: a call that arrives while a pass is running does NOT start a second one (that is how
 *    a photo would be uploaded twice). It queues ONE follow-up pass behind the running one, and every later
 *    caller joins that follow-up - so a capture saved mid-pass is still picked up, and at most one pass is
 *    ever waiting. The follow-up ALWAYS runs, even after a pass that stopped for want of signal: it is what the
 *    `online` event asks for, and a pass that finds no signal again stops at its first request.
 */
export const drainTour = (
  queue: TourQueue,
  transport: TourTransport,
  tourId: string,
  opts: { now?: () => number } = {},
): Promise<DrainSummary> => {
  const inner = internals.get(queue);
  if (!inner) return Promise.reject(new Error('drainTour needs a queue made by openTourQueue.'));
  const now = opts.now ?? (() => queue.now());
  const pass = () => drainOnce(queue, inner, transport, tourId, now);

  const lane = inner.lanes.get(tourId);
  if (!lane) {
    const fresh: Lane = { running: pass(), waiting: null };
    inner.lanes.set(tourId, fresh);
    advanceLane(inner.lanes, tourId, fresh);
    return fresh.running;
  }
  if (!lane.waiting) {
    lane.waiting = lane.running.then(
      () => pass(),
      () => pass(),
    );
  }
  return lane.waiting;
};

/** When the running pass settles, the waiting one (if any) becomes the running one; else the lane is freed. */
const advanceLane = (lanes: Map<string, Lane>, tourId: string, lane: Lane) => {
  const settled = lane.running.then(
    () => undefined,
    () => undefined,
  );
  void settled.then(() => {
    if (lane.waiting) {
      lane.running = lane.waiting;
      lane.waiting = null;
      advanceLane(lanes, tourId, lane);
    } else if (lanes.get(tourId) === lane) {
      lanes.delete(tourId);
    }
  });
};

/**
 * Ask the browser not to evict this origin's storage (spec section 6: Safari evicts script-writable storage
 * after seven days unused). A hint, not a promise - it answers false in Safari tabs. Never throws.
 */
export const requestPersistentStorage = async (): Promise<boolean> => {
  try {
    const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined;
    if (!storage || typeof storage.persist !== 'function') return false;
    if (typeof storage.persisted === 'function' && (await storage.persisted())) return true;
    return await storage.persist();
  } catch {
    return false;
  }
};
